import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { DOG_EXPRESSIONS, HUMAN_EXPRESSIONS } from './expressionCatalog';
import { writesStayLocal } from './localOnly';
import { usePictureUrl } from './pictureLoad';

const ImageGalleryContext = React.createContext(null);

export function generationImageSrc(projectId, generation) {
  if (!generation?.output || generation.error) return null;
  if (generation.output.imageUrl) return generation.output.imageUrl;
  if (!writesStayLocal()) return null;
  if (generation.output.contentUrl) return generation.output.contentUrl;
  if (generation.output.path) {
    return `/content/projects/${projectId}/${generation.output.path}`;
  }
  return null;
}

export function isVideoGeneration(generation) {
  const mime = String(generation?.output?.mimeType || '');
  const file = `${generation?.output?.path || ''} ${generation?.output?.contentUrl || ''}`;
  return mime.startsWith('video/') || /\.mp4(?:$|\?)/i.test(file);
}

/** A few pictures at a time, top of the page first, then left to right. */
const imageLoadQueue = {
  active: 0,
  max: 2,
  jobs: new Set(),
};

function imageLoadPosition(element) {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
  };
}

function pumpImageLoads() {
  const waiting = [...imageLoadQueue.jobs].filter((job) => !job.started && job.element.isConnected);
  waiting.forEach((job) => {
    job.position = imageLoadPosition(job.element);
  });
  waiting.sort((a, b) => {
    const row = Math.round(a.position.top / 48) - Math.round(b.position.top / 48);
    return row || a.position.left - b.position.left;
  });
  while (imageLoadQueue.active < imageLoadQueue.max && waiting.length) {
    const job = waiting.shift();
    job.started = true;
    imageLoadQueue.active += 1;
    job.start();
  }
}

function requestImageLoad(element, start) {
  const job = { element, start, started: false, finished: false };
  imageLoadQueue.jobs.add(job);
  pumpImageLoads();
  return () => {
    if (!imageLoadQueue.jobs.has(job)) return;
    imageLoadQueue.jobs.delete(job);
    if (job.started && !job.finished) {
      job.finished = true;
      imageLoadQueue.active = Math.max(0, imageLoadQueue.active - 1);
      pumpImageLoads();
    }
  };
}

function finishImageLoad(element) {
  const job = [...imageLoadQueue.jobs].find((item) => item.element === element && item.started && !item.finished);
  if (!job) return;
  job.finished = true;
  imageLoadQueue.jobs.delete(job);
  imageLoadQueue.active = Math.max(0, imageLoadQueue.active - 1);
  pumpImageLoads();
}

export function LazyImage({ src, alt, className }) {
  const ref = useRef(null);
  const [activeSrc, setActiveSrc] = useState('');
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || !src) return undefined;
    let cancel = () => {};
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        cancel = requestImageLoad(element, () => setActiveSrc(src));
      },
      { rootMargin: '160px 0px' }
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancel();
    };
  }, [src]);

  useEffect(() => {
    const img = ref.current;
    if (!activeSrc || !img?.complete || !img.naturalWidth) return;
    setShown(true);
    finishImageLoad(img);
  }, [activeSrc]);

  return (
    <img
      ref={ref}
      src={activeSrc || undefined}
      alt={alt || ''}
      className={[className, shown ? 'is-shown' : ''].filter(Boolean).join(' ')}
      decoding="async"
      onLoad={(event) => {
        setShown(true);
        finishImageLoad(event.currentTarget);
      }}
      onError={(event) => finishImageLoad(event.currentTarget)}
    />
  );
}

/** One lightbox for every image or clip mounted in this section. */
export function ImageGallery({ children }) {
  const slidesRef = useRef(new Map());
  const renderOrders = useRef(new Map());
  const publishQueued = useRef(false);
  const [slides, setSlides] = useState([]);
  const [activeId, setActiveId] = useState(null);

  renderOrders.current = new Map();

  const takeOrder = useCallback((id) => {
    const orders = renderOrders.current;
    if (!orders.has(id)) orders.set(id, orders.size + 1);
    return orders.get(id);
  }, []);

  const publish = useCallback(() => {
    if (publishQueued.current) return;
    publishQueued.current = true;
    queueMicrotask(() => {
      publishQueued.current = false;
      setSlides(
        [...slidesRef.current.values()].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
      );
    });
  }, []);

  const register = useCallback(
    (slide) => {
      slidesRef.current.set(slide.id, slide);
      publish();
      return () => {
        if (slidesRef.current.get(slide.id) === slide) {
          slidesRef.current.delete(slide.id);
          publish();
        }
      };
    },
    [publish]
  );

  const open = useCallback((id) => setActiveId(id), []);
  const value = useMemo(
    () => ({ takeOrder, register, open }),
    [takeOrder, register, open]
  );
  const activeIndex = slides.findIndex((slide) => slide.id === activeId);

  return (
    <ImageGalleryContext.Provider value={value}>
      {children}
      <GenerationLightbox
        open={activeIndex >= 0}
        slides={slides}
        index={activeIndex < 0 ? 0 : activeIndex}
        onIndex={(nextIndex) => {
          const next = slides[nextIndex];
          if (next) setActiveId(next.id);
        }}
        onClose={() => setActiveId(null)}
      />
    </ImageGalleryContext.Provider>
  );
}

function expressionCaption(generation) {
  const text = String(generation?.prompt || '');
  if (!text) return '';
  const numbered = [...text.matchAll(/^\s*\d+\)\s*([^—–\n]+?)\s*[—–-]/gm)].map((match) => match[1].trim());
  if (numbered.length) {
    const catalogs = /identical dog|Solo dog/i.test(text)
      ? [DOG_EXPRESSIONS]
      : [HUMAN_EXPRESSIONS, DOG_EXPRESSIONS];
    for (const catalog of catalogs) {
      const emotions = [...new Set(catalog.map((entry) => entry.emotion))];
      for (const emotion of emotions) {
        const labels = catalog
          .filter((entry) => entry.emotion === emotion)
          .sort((a, b) => a.order - b.order)
          .map((entry) => entry.label);
        const same = numbered.length === labels.length && numbered.every((label, i) => label === labels[i]);
        if (same) {
          const name = emotion.charAt(0).toUpperCase() + emotion.slice(1);
          return `${name}: ${labels.join('; ')}`;
        }
      }
    }
    return numbered.join('; ');
  }
  const single = text.match(/Expression\s*[—–-]\s*([^·\n]+?)\s*·\s*([^(\n:]+)/);
  if (!single) return '';
  return `${single[1].trim()}: ${single[2].trim()}`;
}

/** 90% viewer. Arrows walk the images or clips registered in the surrounding section. */
function GenerationLightbox({ open, slides, index, onIndex, onClose }) {
  const slide = slides[index];
  const total = slides.length;
  const video = slide?.media === 'video';

  useEffect(() => {
    if (!open || total < 2) return undefined;
    const onKey = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      event.stopPropagation();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      onIndex((index + delta + total) % total);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, index, total, onIndex]);

  const go = (delta) => {
    if (total < 2) return;
    onIndex((index + delta + total) % total);
  };

  const label = slide?.caption || slide?.alt || (video ? 'Clip' : 'Image preview');
  const noun = video ? 'clip' : 'image';

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width="fit-content"
      className="generation-lightbox"
      wrapClassName="generation-lightbox-root"
      destroyOnHidden
      closable={false}
      maskClosable
      keyboard
      title={label}
      styles={{
        body: { padding: 0, background: 'transparent' },
        header: { display: 'none' },
        container: {
          padding: 0,
          background: 'var(--neutral-950)',
          overflow: 'hidden',
        },
      }}
    >
      <div className="generation-lightbox__frame">
        {slide?.src && video ? (
          <video
            key={slide.id}
            className="generation-lightbox__img"
            src={slide.src}
            controls
            autoPlay
            playsInline
          />
        ) : null}
        {slide?.src && !video ? (
          <img
            className="generation-lightbox__img"
            src={slide.src}
            alt={slide.alt || 'Generation preview'}
          />
        ) : null}
        <button
          type="button"
          className="generation-lightbox__close"
          onClick={onClose}
          aria-label={video ? 'Close clip' : 'Close image preview'}
          autoFocus
        >
          <CloseOutlined />
        </button>
        {total > 1 && (
          <button
            type="button"
            className="generation-lightbox__nav generation-lightbox__nav--prev"
            onClick={() => go(-1)}
            aria-label={`Previous ${noun}`}
          >
            <LeftOutlined />
          </button>
        )}
        {total > 1 && (
          <button
            type="button"
            className="generation-lightbox__nav generation-lightbox__nav--next"
            onClick={() => go(1)}
            aria-label={`Next ${noun}`}
          >
            <RightOutlined />
          </button>
        )}
        <div className="generation-lightbox__meta">
          <p className="generation-lightbox__count" aria-live="polite">
            {label}
            {total > 1 ? ` · ${index + 1} of ${total}` : ''}
          </p>
          {slide?.expressions ? (
            <p className="generation-lightbox__expressions">{slide.expressions}</p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/** Clickable thumb → section lightbox. */
export function GenerationThumb({ projectId, generation, alt, caption, className }) {
  const gallery = useContext(ImageGalleryContext);
  const [open, setOpen] = useState(false);
  const frameRef = useRef(null);
  const [near, setNear] = useState(false);
  const loaded = usePictureUrl(projectId, generation, near);
  const src = generationImageSrc(projectId, generation) || (loaded && loaded !== 'failed' ? loaded : '');
  const failed = !src && loaded === 'failed';
  const video = isVideoGeneration(generation);
  const order = src && gallery ? gallery.takeOrder(generation.id) : 0;
  const expressions = expressionCaption(generation);

  useEffect(() => {
    const element = frameRef.current;
    if (!element || near || src) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setNear(true);
        observer.disconnect();
      },
      { rootMargin: '160px 0px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [near, src]);

  useLayoutEffect(() => {
    if (!gallery || !src || !generation?.id) return undefined;
    return gallery.register({
      id: generation.id,
      src,
      media: video ? 'video' : 'image',
      alt: alt || 'Generation preview',
      caption: caption || '',
      expressions,
      order,
    });
  }, [gallery, src, generation?.id, alt, caption, expressions, order, video]);

  if (!src) {
    return (
      <div
        ref={frameRef}
        className={['generation-thumb', 'generation-thumb--waiting', className].filter(Boolean).join(' ')}
        role="img"
        aria-label={failed ? "Picture didn't load." : 'Waiting for picture'}
      >
        {failed ? <span>Picture didn't load.</span> : null}
      </div>
    );
  }
  const label = alt
    ? `Open larger preview: ${alt}`
    : video
      ? 'Open clip'
      : 'Open larger image preview';
  return (
    <>
      <button
        ref={frameRef}
        type="button"
        className={['generation-thumb', video ? 'generation-thumb--video' : '', className].filter(Boolean).join(' ')}
        onClick={() => (gallery ? gallery.open(generation.id) : setOpen(true))}
        aria-label={label}
      >
        {video ? (
          <video src={`${src}#t=0.1`} muted playsInline preload="metadata" tabIndex={-1} />
        ) : (
          <LazyImage src={src} alt={alt || 'Generation'} />
        )}
      </button>
      {!gallery && (
        <GenerationLightbox
          open={open}
          slides={[{ id: generation.id, src, media: video ? 'video' : 'image', alt, caption, expressions }]}
          index={0}
          onIndex={() => {}}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
